import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import {
  BasePessoaFisicaUseCase,
  type FetchResult,
} from '@/features/proposta/entities/Comprador/use-case/base-pessoa-fisica-use-case'
import { tipoDoVinculoDoCompradorEnum } from '@/features/proposta/interfaces/proposta.enums'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { origemTelefone } from '@/shared/interfaces/cliente.enums'
import type { TCliente } from '@/shared/interfaces/cliente.types'

type TCpfValidatorResponse =
  | { ok: true }
  | { ok: false; reason: 'SAME_AS_COMPRADOR' | 'CONJUGE_ALREADY_LINKED' | 'IS_ALREADY_COMPRADOR' }
export class ConjugeUseCase extends BasePessoaFisicaUseCase<CompradorPessoaFisicaStepSchemaInput['conjuge']> {
  constructor(
    private readonly compradoresPF: TCompradoresCadastrados,
    private readonly compradorAtual: CompradorPessoaFisicaStepSchemaInput['dados'],
    private readonly nacionalidadePrincipalId: string,
  ) {
    super()
  }

  validarCpfConjuge(cpfRaw: string): TCpfValidatorResponse {
    if (this.cpfIgual(cpfRaw, this.compradorAtual.cpf)) return { ok: false, reason: 'SAME_AS_COMPRADOR' }
    const usadoPorOutro = this.compradoresPF.some(
      (comprador) =>
        !this.cpfIgual(comprador.documento, this.compradorAtual.cpf) &&
        comprador.vinculos?.some((vinculo) => vinculo.tipo === 'Conjuge' && this.cpfIgual(vinculo.documento, cpfRaw)),
    )
    const compradorJaCadastrado = this.compradoresPF.find((compradorCadastrado) =>
      this.cpfIgual(compradorCadastrado.documento, cpfRaw),
    )
    if (compradorJaCadastrado) return { ok: false, reason: 'IS_ALREADY_COMPRADOR' }
    if (usadoPorOutro) return { ok: false, reason: 'CONJUGE_ALREADY_LINKED' }
    return { ok: true }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TCliente, AxiosError>,
  ): FetchResult<CompradorPessoaFisicaStepSchemaInput['conjuge']> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) {
      return { type: 'NOT_FOUND', shouldOpenModal: false }
    }

    if (result.isSuccess && result.data) {
      const cliente = result.data
      return {
        type: 'SUCCESS',
        patch: {
          contato: {
            email: cliente.email ?? '',
            telefone: {
              origem: cliente.telefone?.origem ?? 'Brasil',
              numero: cliente.telefone?.numero ?? '',
              principal: true,
            },
          },
          dados: {
            id: cliente.id,
            cpf: cliente.cpf ?? '',
            nome: cliente.nome,
            email: null,
            profissaoId: cliente.profissao?.id ?? '',
            nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
            maiorDe18Anos: 'sim',
            buscouCpf: true,
          },
          imagem: cliente.imagem ?? null,
          tipo: tipoDoVinculoDoCompradorEnum.Conjuge,
        },
      }
    }

    return { type: 'ERROR' }
  }

  limparFormulario(): CompradorPessoaFisicaStepSchemaInput['conjuge'] {
    return {
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: null,
        profissaoId: '',
        nacionalidadeId: this.nacionalidadePrincipalId,
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
      tipo: tipoDoVinculoDoCompradorEnum.Conjuge,
    }
  }

  static inicializarDefaultValues(nacionalidadePrincipalId?: string): CompradorPessoaFisicaStepSchemaInput['conjuge'] {
    return {
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: null,
        profissaoId: '',
        nacionalidadeId: nacionalidadePrincipalId ?? '',
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
      tipo: tipoDoVinculoDoCompradorEnum.Conjuge,
    }
  }
}
